import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';

import Header from './Header';
import PostForm from './PostForm';
import PostList from './PostList';
import MediaModal from './MediaModal';

import { useAuth } from '../context/AuthContext';
import '../App.css';
import 'leaflet/dist/leaflet.css';

function MainApp() {
  const [posts, setPosts] = useState([]);
  const [modalMediaUrl, setModalMediaUrl] = useState(null);
  const [modalPosterUrl, setModalPosterUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);

  const { user, logout, authChecked } = useAuth();

  const [timezones, setTimezones] = useState([]);
  const [loadingTimezones, setLoadingTimezones] = useState(true);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get('/api/posts/');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authChecked) {
      fetchPosts();
    }
  }, [authChecked, fetchPosts]);

  useEffect(() => {
    async function fetchTimezones() {
      try {
        const response = await axios.get('/api/timezones/');
        setTimezones(response.data.timezones);
      } catch (error) {
        console.error('Error fetching timezones:', error);
      } finally {
        setLoadingTimezones(false);
      }
    }
    fetchTimezones();
  }, []);

  const handleEdit = (post) => {
    setEditingPost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      axios
        .delete(`/api/posts/${postId}/`)
        .then(() => fetchPosts())
        .catch((error) => console.error('Error deleting post:', error));
    }
  };

  const handlePostSubmit = async (formData, postId) => {
    try {
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (postId) {
        await axios.put(`/api/posts/${postId}/`, formData, config);
      } else {
        await axios.post('/api/posts/', formData, config);
      }
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Error submitting post:', error);
    }
  };

  const handleMediaClick = (mediaUrl, posterUrl) => {
    setModalMediaUrl(mediaUrl);
    setModalPosterUrl(posterUrl);
  };

  if (!authChecked) {
    return <p>Checking authentication...</p>;
  }

  return (
    <div className="App">
      <Header onLogout={logout} />

      {user?.is_superuser && (
        <div className="form-and-dropzone" style={{ display: 'flex', gap: '20px' }}>
          <div className="new-post-form" style={{ flex: 1 }}>
            <h2>{editingPost ? 'Edit Post' : 'Create a New Post'}</h2>
            <PostForm
              post={editingPost || {}}
              isEditing={!!editingPost}
              onSave={handlePostSubmit}
              onCancel={() => setEditingPost(null)}
              timezones={timezones}
            />
          </div>
        </div>
      )}

      <hr style={{ margin: '40px 0' }} />

      <div className="posts-container">
        {isLoading ? (
          <p>Loading posts...</p>
        ) : posts.length === 0 ? (
          <p>No posts found.</p>
        ) : (
          <PostList
            posts={posts}
            user={user}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onImageClick={handleMediaClick}
          />
        )}
      </div>

      <MediaModal
        mediaUrl={modalMediaUrl}
        posterUrl={modalPosterUrl}
        onClose={() => {
          setModalMediaUrl(null);
          setModalPosterUrl(null);
        }}
      />
    </div>
  );
}

export default MainApp;
